import { TWITTER_AUTOMATION_STAGE_STATUSES } from './twitterAutomationStageStatuses'
import * as twitterUrls from './twitterUrls'
import { XSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { StageType, TwitterRecommendationKey } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import {
  getCurrentValueForCheckbox,
  saveCurrentValueForCheckbox,
  toggleCheckbox,
} from '../checkboxDetection'
import {
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { shouldUpdateSetting } from '../utils'

export const getCantUndoFn = () => defaultCantUndoFn

export const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => (value ? 'On' : 'Off'))

export function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
export function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

export const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => value === false)

const mapSettingKeyToText = {
  [XSettingKey.AI_OPT_OUT]:
    'Allow your public data as well as your interactions, inputs, and results with Grok and xAI to be used for training and fine-tuning',
  [XSettingKey.AI_PERSONALIZATION]:
    'Allow X to personalize your experience with Grok',
  [XSettingKey.AI_CONVERSATION_HISTORY]:
    'Allow Grok to remember your conversation history',
}

enum StageKey {
  GATHER_SETTING_DISCOVERABLE_AI_OPT_OUT = 'GATHER_SETTING_DISCOVERABLE_AI_OPT_OUT',
  GATHER_SETTING_DISCOVERABLE_AI_PERSONALIZATION = 'GATHER_SETTING_DISCOVERABLE_AI_PERSONALIZATION',
  GATHER_SETTING_DISCOVERABLE_AI_CONVERSATION_HISTORY = 'GATHER_SETTING_DISCOVERABLE_AI_CONVERSATION_HISTORY',
  UPDATE_SETTING_DISCOVERABLE_AI_OPT_OUT = 'UPDATE_SETTING_DISCOVERABLE_AI_OPT_OUT',
  UPDATE_SETTING_DISCOVERABLE_AI_PERSONALIZATION = 'UPDATE_SETTING_DISCOVERABLE_AI_PERSONALIZATION',
  UPDATE_SETTING_DISCOVERABLE_AI_CONVERSATION_HISTORY = 'UPDATE_SETTING_DISCOVERABLE_AI_CONVERSATION_HISTORY',
  UNDO_SETTING_DISCOVERABLE_AI_OPT_OUT = 'UNDO_SETTING_DISCOVERABLE_AI_OPT_OUT',
  UNDO_SETTING_DISCOVERABLE_AI_PERSONALIZATION = 'UNDO_SETTING_DISCOVERABLE_AI_PERSONALIZATION',
  UNDO_SETTING_DISCOVERABLE_AI_CONVERSATION_HISTORY = 'UNDO_SETTING_DISCOVERABLE_AI_CONVERSATION_HISTORY',
}

const settingToGatherStageKey = {
  [XSettingKey.AI_OPT_OUT]: StageKey.GATHER_SETTING_DISCOVERABLE_AI_OPT_OUT,
  [XSettingKey.AI_PERSONALIZATION]:
    StageKey.GATHER_SETTING_DISCOVERABLE_AI_PERSONALIZATION,
  [XSettingKey.AI_CONVERSATION_HISTORY]:
    StageKey.GATHER_SETTING_DISCOVERABLE_AI_CONVERSATION_HISTORY,
}

const settingToUpdateStageKey = {
  [XSettingKey.AI_OPT_OUT]: StageKey.UPDATE_SETTING_DISCOVERABLE_AI_OPT_OUT,
  [XSettingKey.AI_PERSONALIZATION]:
    StageKey.UPDATE_SETTING_DISCOVERABLE_AI_PERSONALIZATION,
  [XSettingKey.AI_CONVERSATION_HISTORY]:
    StageKey.UPDATE_SETTING_DISCOVERABLE_AI_CONVERSATION_HISTORY,
}

const settingToUndoStageKey = {
  [XSettingKey.AI_OPT_OUT]: StageKey.UNDO_SETTING_DISCOVERABLE_AI_OPT_OUT,
  [XSettingKey.AI_PERSONALIZATION]:
    StageKey.UNDO_SETTING_DISCOVERABLE_AI_PERSONALIZATION,
  [XSettingKey.AI_CONVERSATION_HISTORY]:
    StageKey.UNDO_SETTING_DISCOVERABLE_AI_CONVERSATION_HISTORY,
}

export const gatherStageDefs: StageDef[] = Object.keys(
  settingToGatherStageKey,
).map((settingKey) => ({
  type: StageType.ACTION,
  key: settingToGatherStageKey[settingKey],
  status: TWITTER_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
  url: twitterUrls.aiSettingsUrl,
  action: async (machineContext, userContext) =>
    saveCurrentValueForCheckbox(
      userContext.updateStoredAutomationErrors,
      userContext.updateStoredInitialValues,
      mapSettingKeyToText,
      settingKey,
      TwitterRecommendationKey.TWITTER_AI_OPT_OUT,
    ),
}))

export const updateStageDefs: StageDef[] = Object.keys(
  settingToUpdateStageKey,
).map((settingKey) => ({
  type: StageType.ACTION,
  key: settingToUpdateStageKey[settingKey],
  status: TWITTER_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
  url: twitterUrls.aiSettingsUrl,
  action: async (machineContext, userContext) => {
    const { settingsSelected, initialValues, isDry } = userContext
    const shouldUpdate = shouldUpdateSetting(
      settingsSelected,
      initialValues,
      settingKey,
      getIsFulfilledFn,
    )

    if (!shouldUpdate) {
      return null
    }

    const currentValue = await getCurrentValueForCheckbox(
      mapSettingKeyToText[settingKey],
    )

    if (!currentValue) {
      return null
    }

    if (isDry) {
      return null
    }

    return toggleCheckbox(mapSettingKeyToText[settingKey])
  },
}))

export const undoStageDefs: StageDef[] = Object.keys(settingToUndoStageKey).map(
  (settingKey) => ({
    type: StageType.ACTION,
    key: settingToUndoStageKey[settingKey],
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: twitterUrls.aiSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext
      if (!settingsSelected?.[settingKey]) {
        return null
      }

      const prevValue = initialValues[settingKey]
      const currentValue = await getCurrentValueForCheckbox(
        mapSettingKeyToText[settingKey],
      )

      if (prevValue === currentValue) {
        return null
      }

      if (isDry) {
        return null
      }

      return toggleCheckbox(mapSettingKeyToText[settingKey])
    },
  }),
)
